import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { Dialog, DialogTitle, DialogBody, DialogActions } from './dialog';
import { Button } from './button';
import { Input } from './input';
import { Text } from './text';
import { Checkbox } from './checkbox';
import { Textarea } from './textarea';
import { XCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { useUser } from './UserContext';
import { logBitacora } from '../utils/bitacora';
import { formatEventoTipos } from '../utils/fianzaEventos';

const ATTACHMENT_LABELS = {
  fianzaPdf: 'Fianza (PDF)',
  facturaPdf: 'Factura (PDF)',
  fianzaXml: 'Fianza (XML)',
  facturaXml: 'Factura (XML)',
};

// Attachable files grouped by document type (one column per type in the
// dialog), each with its versions: the fianza's root filePaths (emisión) plus
// every endoso evento's own files. Version values are what the backend
// expects: "<key>" for root files, "evento:<id>:<key>" for endoso files.
const ATTACHMENT_TYPE_ORDER = ['fianzaPdf', 'fianzaXml', 'facturaPdf', 'facturaXml'];

function buildAttachmentGroups(filePaths, eventos) {
  return ATTACHMENT_TYPE_ORDER.map((key) => {
    const versions = [];
    if (filePaths && filePaths[key]) {
      versions.push({ value: key, label: 'Emisión' });
    }
    (eventos || []).forEach((ev) => {
      if (!ev || !ev.id || ev.tipo === 'emision') return;
      if (ev.filePaths && ev.filePaths[key]) {
        const fecha = ev.fecha
          ? new Date(ev.fecha).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })
          : '';
        versions.push({
          value: `evento:${ev.id}:${key}`,
          label: `${formatEventoTipos(ev)}${fecha ? ` ${fecha}` : ''}`,
        });
      }
    });
    return { key, label: ATTACHMENT_LABELS[key], versions };
  }).filter((group) => group.versions.length > 0);
}

/**
 * Send-fianza-by-email dialog, shared by the post-upload notify step
 * (UploadFianza) and the quick-send button on contrato fianza rows.
 * Loads its own recipients: the cliente's correoContacto plus every client
 * account with access to the cliente — all preselected, each deselectable.
 */
export function NotifyFianzaDialog({
  open,
  onClose,
  clientId,
  clientName,
  contratoId,
  fianzaId,
  numeroFianza,
  filePaths,
  eventos,
  title = 'Enviar fianza por correo',
  warning,
  onSent,
}) {
  const { user, userData } = useUser();
  const [recipients, setRecipients] = useState(null); // null while loading
  const [customEmail, setCustomEmail] = useState('');
  const [message, setMessage] = useState('');
  const [attachSelected, setAttachSelected] = useState({});
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const attachmentGroups = buildAttachmentGroups(filePaths, eventos);
  const attachmentValues = attachmentGroups.flatMap((g) => g.versions.map((v) => v.value));

  useEffect(() => {
    if (!open || !clientId) return;
    let cancelled = false;
    setRecipients(null);
    setCustomEmail('');
    setMessage('');
    setError(null);
    // All available files (emisión + endosos) start attached; each can be
    // unchecked.
    setAttachSelected(buildAttachmentGroups(filePaths, eventos)
      .flatMap((g) => g.versions)
      .reduce((acc, v) => ({ ...acc, [v.value]: true }), {}));
    (async () => {
      const list = [];
      const seen = new Set();
      try {
        const clienteSnap = await getDoc(doc(db, 'clientes', clientId));
        const clienteData = clienteSnap.exists() ? clienteSnap.data() : {};
        if (clienteData.correoContacto && clienteData.correoContacto.includes('@')) {
          seen.add(clienteData.correoContacto.toLowerCase());
          list.push({
            email: clienteData.correoContacto,
            nombre: clienteData.nombreContacto || clienteData.nombreEmpresa || '',
            hasAccount: false,
            selected: true,
          });
        }
        if (userData?.org) {
          const accountsSnap = await getDocs(query(
            collection(db, 'usuarios'),
            where('org', '==', userData.org),
            where('rol', '==', 'client'),
            where('clientIds', 'array-contains', clientId),
          ));
          accountsSnap.forEach((accountDoc) => {
            const account = accountDoc.data();
            if (account.correo && account.correo.includes('@') && !seen.has(account.correo.toLowerCase())) {
              seen.add(account.correo.toLowerCase());
              list.push({
                email: account.correo,
                nombre: account.nombre || '',
                hasAccount: true,
                selected: true,
              });
            }
          });
        }
      } catch (e) {
        console.error('Failed to load notify recipients:', e);
      }
      if (!cancelled) setRecipients(list);
    })();
    return () => { cancelled = true; };
    // filePaths/eventos are read once at open on purpose: live parent
    // refreshes mid-dialog must not reset the user's selections.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId, userData?.org]);

  const toggleRecipient = (email) => {
    setRecipients((prev) => prev && prev.map((r) =>
      r.email === email ? { ...r, selected: !r.selected } : r
    ));
  };

  const addCustomEmail = () => {
    const email = customEmail.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
    setRecipients((prev) => {
      const list = prev || [];
      if (list.some((r) => r.email.toLowerCase() === email.toLowerCase())) {
        return list.map((r) =>
          r.email.toLowerCase() === email.toLowerCase() ? { ...r, selected: true } : r
        );
      }
      return [...list, { email, nombre: '', hasAccount: false, custom: true, selected: true }];
    });
    setCustomEmail('');
  };

  const handleSend = async () => {
    const selected = (recipients || []).filter((r) => r.selected);
    if (selected.length === 0) return;
    setSending(true);
    setError(null);
    try {
      const send = httpsCallable(getFunctions(), 'sendFianzaEmail');
      const res = await send({
        contratoId,
        fianzaId,
        recipients: selected.map((r) => r.email),
        message: message.trim(),
        attachments: attachmentValues.filter((value) => attachSelected[value]),
      });
      const sentTo = res.data?.sent || selected.map((r) => r.email);
      try {
        await logBitacora({
          action: 'UPDATE',
          collection: 'contracts',
          documentId: contratoId,
          metadata: {
            clientId,
            clientName: clientName || 'Sin nombre',
            contratoId,
            description: `Envió fianza #${numeroFianza || 'sin número'} por correo a ${sentTo.join(', ')}`,
          },
          user,
          userData,
        });
      } catch (logError) {
        console.error('Failed to log fianza email:', logError);
      }
      if (onSent) onSent(sentTo);
      onClose();
    } catch (err) {
      console.error('Error sending fianza emails:', err);
      setError(err?.message || 'No se pudo enviar el correo.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog size="md" open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogBody>
        <div className="space-y-4">
          {numeroFianza && numeroFianza !== 'sin número' && (
            <Text className="text-sm text-zinc-600">Fianza #{numeroFianza}</Text>
          )}
          {warning && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <Text className="text-sm text-amber-800">{warning}</Text>
            </div>
          )}
          {recipients === null ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-azul-surelia"></div>
            </div>
          ) : (
            <>
              {recipients.length === 0 ? (
                <Text className="text-sm text-zinc-600">
                  El cliente no tiene correos registrados.
                </Text>
              ) : (
                <div className="border border-azul-surelia/15 rounded-lg divide-y divide-azul-surelia/10">
                  {recipients.map((r) => (
                    <div
                      key={r.email}
                      onClick={() => toggleRecipient(r.email)}
                      className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-azul-surelia/5 transition-colors"
                    >
                      <span onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={r.selected}
                          onChange={() => toggleRecipient(r.email)}
                        />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-zinc-800 font-medium truncate">
                          {r.nombre || r.email}
                        </div>
                        {r.nombre && (
                          <div className="text-xs text-zinc-500 truncate">{r.email}</div>
                        )}
                      </div>
                      <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                        r.hasAccount
                          ? 'bg-azul-surelia/10 text-azul-surelia border-azul-surelia/20'
                          : 'bg-zinc-100 text-zinc-600 border-zinc-200'
                      }`}>
                        {r.hasAccount ? 'Cuenta cliente' : r.custom ? 'Otro' : 'Contacto'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={customEmail}
                  onChange={(e) => setCustomEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustomEmail();
                    }
                  }}
                  placeholder="Agregar otro correo"
                  className="flex-1"
                />
                <Button
                  color="white"
                  onClick={addCustomEmail}
                  disabled={!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(customEmail.trim())}
                >
                  Agregar
                </Button>
              </div>
              {attachmentGroups.length > 0 && (
                <div>
                  <Text className="text-xs font-medium text-zinc-500 mb-1.5">Adjuntos</Text>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {attachmentGroups.map((group) => (
                      <div key={group.key}>
                        <Text className="text-xs font-semibold text-azul-surelia mb-1.5">{group.label}</Text>
                        <div className="space-y-1.5">
                          {group.versions.map((v) => (
                            <label key={v.value} className="flex items-center gap-1.5 cursor-pointer">
                              <Checkbox
                                checked={attachSelected[v.value] || false}
                                onChange={() => setAttachSelected((prev) => ({
                                  ...prev,
                                  [v.value]: !prev[v.value],
                                }))}
                              />
                              <span className="text-xs text-zinc-700">{v.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Textarea
                rows={2}
                maxLength={500}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Mensaje opcional"
              />
            </>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0" />
              <Text className="text-sm text-red-800">{error}</Text>
            </div>
          )}
        </div>
      </DialogBody>
      <DialogActions>
        <Button color="white" onClick={onClose} disabled={sending}>
          {recipients && recipients.length === 0 ? 'Cerrar' : 'Omitir'}
        </Button>
        {recipients !== null && (
          <Button
            onClick={handleSend}
            disabled={sending || !recipients.some((r) => r.selected)}
          >
            {sending ? 'Enviando...' : 'Enviar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default NotifyFianzaDialog;
